import Images from '../../common/Images'
import MarginDecoration from '../../common/components/MarginDecoration/MarginDecoration'
import PageLayout from '../../common/components/PageLayout/PageLayout'
import References from '../../common/components/References/References'
import TextRef from '../../common/components/TextRef/TextRef'
import TextSeparator from '../../common/components/TextSeparator/TextSeparator'
import Title from '../../common/components/Title/Title'
import './Description.css'

function Description () {
  return (
    <PageLayout title="Project Description">
      <MarginDecoration />
      <div className="description">
        <Title title="Context" isMid />
        <p>
          Blood donations are a staple of modern medicine across the world. They
          allow surgeons and healthcare professionals to save the lives of
          thousands of patients each year. Blood donations come from blood
          banks, which rely on donations from healthy volunteers to maintain a
          safe blood stock sufficient to supply human populations.
          Unfortunately, this leads to blood banks chronically running low on
          specific blood types needed by vulnerable patients. We have chosen
          this project to propose an innovative solution to help blood banks
          combat this need. The vital importance of blood donations and
          transfusions was taught to our team members from a very young age
          through extensive promotion by our provincial non-profit blood bank
          (Héma-Québec). The virtue of giving blood is thus dear to each and
          every team member.
        </p>
        <TextSeparator />
        <Title title="The project" isMid />
        <p>
          <img className="description-poche" src={Images.Poche} alt="" />
          <i>
            <b>Blood Cell Barber Shop</b>
          </i>{' '}
          concerns the entire world population. By proposing a way to produce
          universal blood using synthetic biology, we aspire to improve the
          process of blood donations and blood transfusions.
          <br />
          <br />
          Our project aims at making universal blood to help supply blood
          transfusions for people with uncommon blood types by removing blood
          cells antigens. O- blood type is considered a universal blood type
          since it can be given to anyone, no matter their blood type. Using
          synthetic biology, our project innovates on past enzymatic antigen
          removal treatments by using extensive protein modeling, protein
          structure prediction, and molecular dynamics, as well as optimizing an
          A and B-cleaving enzyme system, resulting in O-type blood from blood
          donations. Our process aims to be usable in the clinical field. In
          contrast to previous iGEM projects, we aim to design an optimized
          combined enzymatic removal of antigens filtering device. In
          perspective, our enzymatic treatment could also have the potential of
          facilitating xenotransplantations by tissue treatment.
        </p>
        <br />
        <br />
        <br />
        <Title title="Details" isMid />

        <p>
          In Canada, only 50% of the population is allowed to give blood
          <TextRef sup="1" content="" />. This is mainly due to the exhaustive
          list of biological and ethical constraints required to ensure the
          donation of safe blood. The main requirements are to be at least 17
          years old and have a specific height and weight when under 23 years
          old
          <TextRef sup="2" content="" />. In addition, the donor must be healthy
          and free of blood diseases. However, in reality, only one person out
          of 81 ever makes a blood donation
          <TextRef sup="1" content="" />. Nevertheless, over one million blood
          products have been distributed across 700 hospitals
          <TextRef sup="3" content="" />. Even though this statistic sounds big,
          it is well below the current needs. In fact, not every donation can be
          given to every patient because of blood type incompatibilities. Blood
          types aren’t equally common, and this means that there are rarer blood
          types. Because of this, people with uncommon blood types sometimes do
          not receive a transfusion because the blood bank cannot supply it.
        </p>
        <p>
          According to the World Health Organization (WHO), there exists a
          significant difference in the level of access to blood between low-
          and high-income countries. In fact, in high-income countries, the
          median blood donation rate is 31.5 donations per 1000 people, while in
          low-income countries, this donation rate drops to 5 donations per 1000
          people
          <TextRef sup="4" content="" />. This donation rate is also an
          indicator for the availability of blood. Internationally, the demand
          for safe blood is high. Our project contributes to solving this
          problem and it can help save many lives, both locally and worldwide.
        </p>
        <p>
          Extensive work has already been done in order to convert regular blood
          donations to universal blood. It is current knowledge that the only
          blood type that can be donated universally is the type O- blood
          <TextRef sup="5" content="" />. It is also known that type B blood
          only differs from type O blood by an additional galactose moiety, a
          sugar antigen, whereas type A blood differs from type O blood by its
          addition of a sugar antigen N-Acetylgalactosamine
          <TextRef sup="5" content="" />. The first B-to-O conversion was done
          by Goldstein in 1982, but was not optimized and required a large
          amount of enzyme and thus had a low efficacy
          <TextRef sup="5" content="" />. In 2008, Liu et al. identified a new
          GH110 subfamily of α1,3-Galactosidases which has a higher performance
          in the removal of the type B blood antigen
          <TextRef sup="6" content="" />. In 2007, identification of new
          α-galactosidases and α-N-acetylgalactosaminidase allowed the
          conversion of type A blood to type O blood, but it was under strict
          conditions
          <TextRef sup="5" content="" />. Both conversions are still unused in
          clinical settings for various reasons. One of those reasons is the low
          efficacy and efficiency of the B and A-cleaving enzymes
          <TextRef sup="5" content="" />. This problem might arise from the
          incomplete cleavage of the type B-antigen, thus when cross-matched
          with serum from A-type and O-type, the positive results are low
          <TextRef sup="5" content="" />.
        </p>
        <p>
          This is why, this year, we decided to work on modelling and optimizing
          A and B-cleaving enzymes and to build a process that can be used in
          the clinical field. In particular, our proposed system for type A
          antigen removal utilizes a bipartite reaction, not requiring
          additional cofactors and matrix.
        </p>
      </div>

      <References
        references={[
          <span key={1}>
            <i>
              Attendance lags at blood and plasma donor centres; over 150,000
              open appointments need to be filled this summer.
            </i>{' '}
            https://www.blood.ca/en/about-us/media/newsroom/attendance-lags-blood-and-plasma-donor-centres-over-150000-open#:~:text=JUNE%2012%2C%202023%20(OTTAWA),fewer%20people%20are%20donating%20regularly.
            Consulted September 23, 2023.
          </span>,
          <span key={2}>
            <i>ABCs of Eligibility to Donating Blood.</i>{' '}
            https://www.blood.ca/en/blood/am-i-eligible-donate-blood/abcs-eligibility.
            Consulted September 23, 2023.
          </span>,
          <span key={3}>
            <i>
              Canadian Blood Services is rallying people across the country to
              shine a light on the need for new donors.
            </i>{' '}
            https://www.blood.ca/en/about-us/media/newsroom/canadian-blood-services-rallying-people-across-country-shine-light-need-new#:~:text=There%20are%20over%2017.5%20million,country%2C%20but%20this%20is%20unsustainable.
            Consulted September 23, 2023.
          </span>,
          <span key={4}>
            <i>Blood Safety and Availability.</i>{' '}
            https://www.who.int/news-room/fact-sheets/detail/blood-safety-and-availability.
            Consulted September 23, 2023.
          </span>,
          <span key={5}>
            Rahfeld, P., & Withers, S. G. (2020). Toward universal donor blood:
            Enzymatic conversion of A and B to O type.{' '}
            <i>Journal of Biological Chemistry</i>, 295(2), 325-334.
          </span>,
          <span key={6}>
            Liu, Q. P., Yuan, H., Bennett, E. P., Levery, S. B., Nudelman, E.,
            Spence, J., ... & Clausen, H. (2008). Identification of a GH110
            subfamily of &alpha;1, 3-galactosidases: novel enzymes for removal
            of the &alpha;3Gal xenotransplantation antigen.{' '}
            <i>Journal of Biological Chemistry</i>, 283(13), 8545-8554.
          </span>
        ]}
      />
    </PageLayout>
  )
}

export default Description
